import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../utils/api";
import { toast } from "react-toastify";


export const getMatchedProductsThunk = createAsyncThunk(
    'bom/getMatchedProductsThunk',
    async (data, { rejectWithValue, dispatch }) => {
        const normalizePartNumber = (value) => String(value ?? '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();

        const findExactSearchResult = (results, partNumber) => {
            const requestedPartNumber = normalizePartNumber(partNumber);

            return results.find((result) => {
                if (result?.type !== 'product') return false;

                const productNumbers = [
                    result?.name,
                    ...(Array.isArray(result?.productOverview?.productNumber)
                        ? result.productOverview.productNumber
                        : [result?.productOverview?.productNumber])
                ];

                return productNumbers.some((number) =>
                    normalizePartNumber(number) === requestedPartNumber
                );
            });
        };

        try {
            const response = await api.getMatchedProducts(data);
            const matchedRows = Array.isArray(response.data?.data)
                ? response.data.data
                : data.map((item) => ({ ...item, status: false }));

            // The BOM endpoint and the catalogue search endpoint can briefly be on
            // different deployments. Verify unmatched rows against the live catalogue
            // so an existing exact part is never incorrectly shown as "not found".
            const unmatchedIndexes = matchedRows
                .map((item, index) => ({ item, index }))
                .filter(({ item }) => !item?.status && normalizePartNumber(item?.productNumber));
            const concurrentLookups = Math.min(3, unmatchedIndexes.length);
            let nextLookupIndex = 0;

            // Never flood the remote catalogue with one request per row at once.
            // A small queue keeps the BOM page responsive for large spreadsheets.
            void Promise.all(Array.from({ length: concurrentLookups }, async () => {
                while (nextLookupIndex < unmatchedIndexes.length) {
                    const lookup = unmatchedIndexes[nextLookupIndex++];
                    const partNumber = lookup.item.productNumber;

                    try {
                        const searchResponse = await api.smartSearch(
                            `?query=${encodeURIComponent(String(partNumber).trim())}&limit=10`,
                            { timeout: 5000 }
                        );
                        const product = findExactSearchResult(
                            searchResponse.data?.data?.results || [],
                            partNumber
                        );

                        if (product) dispatch(setMatchedFallbackProduct({
                            productNumber: partNumber,
                            product
                        }));
                    } catch (searchError) {
                        // The primary BOM response remains usable if a fallback lookup
                        // times out or the catalogue search is temporarily unavailable.
                        console.log(searchError);
                    }
                }
            }));

            return matchedRows;
        } catch (error) {
            console.log(error);
            return rejectWithValue(error?.response?.data?.message || 'Unable to match BOM products');
        }
    }
);

export const getAlterProductsThunk = createAsyncThunk(
    'bom/getAlterProductsThunk  ',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.getAlterProducts(data);
            console.log(response.data);

            return response.data?.data;
        } catch (error) {
            console.log(error);
        }
    }
);

export const sendBomEnquiryThunk = createAsyncThunk(
    'bom/sendBomEnquiryThunk  ',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.sendBomEnquiry(data);
            console.log(response.data);
            toast.success('BOM Enquiry sent successfully')
            return response.status;

        } catch (error) {
            console.log(error);
            toast.error(error?.response?.data?.message || 'something went wronf')
        }
    }
);

export const getBomEnquiriesSlice = createAsyncThunk(
    'bom/getBomEnquiriesSlice',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.getBomEnquiry(data);
            return response.data;
        } catch (error) {
            console.log(error);
        }
    }
);

export const getBomEnquirydetailsThunk = createAsyncThunk(
    'bom/getBomEnquirydetailsThunk',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.getBomEnquirydetails(data);
            return response.data?.data;
        } catch (error) {
            console.log(error);
        }
    }
);


export const getWizardEnquiriesSlice = createAsyncThunk(
    'bom/getWizardEnquiriesSlice',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.getWizardEnquiry(data);
            return response.data;
        } catch (error) {
            console.log(error);
        }
    }
);

export const getWizardEnquirydetailsThunk = createAsyncThunk(
    'bom/getWizardEnquirydetailsThunk',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.getWizardEnquirydetails(data);
            return response.data?.data;
        } catch (error) {
            console.log(error);
        }
    }
);


export const getExcessEnquiriesSlice = createAsyncThunk(
    'bom/getExcessEnquiriesSlice',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.getExcessEnquiry(data);
            return response.data;
        } catch (error) {
            console.log(error);
        }
    }
);

export const getExcessEnquirydetailsThunk = createAsyncThunk(
    'bom/getExcessEnquirydetailsThunk',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.getExcessEnquirydetails(data);
            return response.data?.data;
        } catch (error) {
            console.log(error);
        }
    }
);





const bomSlice = createSlice({
    name: 'bom',
    initialState: {
        bompreview: [],
        macthedProducts: [],
        alterProducts: {},
        loading: false,
        refresh: false,
        alterLoading: false,
        bomEnquiries: [],
        page: 1,
        bomenquirydetails: {},
        filename: null,
        inputValue: '',
        quantities: {},
        selectedProducts: [],
        activeTab :'BOM'
    },
    reducers: {
        setBomPreview: (state, action) => {
            state.bompreview = action.payload
        },
        deletBomPreviewData: (state, action) => {
            state.bompreview = []
            state.filename = null
            state.selectedProducts=[]
        },
        setFileName: (state, action) => {
            state.filename = action.payload
        },
        setInputValue: (state, action) => {
            state.inputValue = action.payload
        },
        deleteInputValue: (state, action) => {
            state.inputValue = ''
        },
        setQuantities: (state, action) => {
            state.quantities = { ...state.quantities, ...action.payload };
        },
        setSelectedProducts: (state, action) => {
            state.selectedProducts = action.payload;
        },
        setMatchedFallbackProduct: (state, action) => {
            const normalizePartNumber = (value) => String(value ?? '')
                .trim()
                .replace(/\s+/g, ' ')
                .toLowerCase();
            const requestedPartNumber = normalizePartNumber(action.payload?.productNumber);

            state.macthedProducts = state.macthedProducts.map((item) =>
                normalizePartNumber(item?.productNumber) === requestedPartNumber
                    ? { ...item, product: action.payload.product, status: true }
                    : item
            );
        },
        setBomPage: (state, action) => {
            state.page = action.payload
        },
        setBomEnquiry: (state, action) => {
            state.bomEnquiries = []
        },
        // ✅ Toggle product selection (add/remove)
        toggleProductSelection: (state, action) => {
            const { productId, isChecked } = action.payload;

            if (isChecked) {
                state.quantities[productId] = state.quantities[productId] || 1; // Default to 1 if not set
            } else {
                delete state.quantities[productId]; // Remove from selection
            }
        },
        updateQuantity: (state, action) => {
            const { productNumber, quantity } = action.payload;
            state.macthedProducts = state.macthedProducts.map(product =>
                product?.productNumber === productNumber
                    ? { ...product, quantity: parseInt(quantity) || '' }
                    : product
            );
            state.selectedProducts = state.selectedProducts.map(product =>
                product?.productNumber === productNumber ? { ...product, quantity: parseInt(quantity) || ''  }
                    : product
            )
        },

        deleteAlterProduct: (state, action) => {
            const partNumberToRemove = action.payload;

            state.alterProducts.alternateProducts = state.alterProducts.alternateProducts?.filter(
                (i) => {
                    const productNumber = Array.isArray(i?.productOverview?.productNumber)
                        ? i.productOverview.productNumber.join("")
                        : i?.productOverview?.productNumber;

                    return productNumber !== partNumberToRemove;
                }
            );
        },
     
        setActiveTab:(state,action)=>{
            state.activeTab = action.payload
        }  

    },
    extraReducers: (builder) => {
        builder
            .addCase(getMatchedProductsThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getMatchedProductsThunk.fulfilled, (state, action) => {
                state.loading = false;
                const payload = Array.isArray(action.payload) ? action.payload : [];
                state.macthedProducts = payload.filter(item => {
                    const p = item?.productNumber ? String(item.productNumber).trim().toLowerCase() : '';
                    return p && p !== 'id' && p !== '-';
                });
            })
            .addCase(getMatchedProductsThunk.rejected, (state, action) => {
                state.loading = false;
            })


            .addCase(getAlterProductsThunk.pending, (state) => {
                state.alterLoading = true;
            })
            .addCase(getAlterProductsThunk.fulfilled, (state, action) => {
                state.alterLoading = false;
                state.alterProducts = action.payload
            })
            .addCase(getAlterProductsThunk.rejected, (state, action) => {
                state.alterLoading = false;
            })

            .addCase(getBomEnquiriesSlice.pending, (state) => {
                state.loading = true;
            })
            .addCase(getBomEnquiriesSlice.fulfilled, (state, action) => {
                state.loading = false;
                state.bomEnquiries = action.payload
            })
            .addCase(getBomEnquiriesSlice.rejected, (state, action) => {
                state.loading = false;
            })

            .addCase(getBomEnquirydetailsThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getBomEnquirydetailsThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.bomenquirydetails = action.payload
            })
            .addCase(getBomEnquirydetailsThunk.rejected, (state, action) => {
                state.loading = false;
            })


            .addCase(getWizardEnquiriesSlice.pending, (state) => {
                state.loading = true;
            })
            .addCase(getWizardEnquiriesSlice.fulfilled, (state, action) => {
                state.loading = false;
                state.bomEnquiries = action.payload
            })
            .addCase(getWizardEnquiriesSlice.rejected, (state, action) => {
                state.loading = false;
            })


            .addCase(getWizardEnquirydetailsThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getWizardEnquirydetailsThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.bomenquirydetails = action.payload
            })
            .addCase(getWizardEnquirydetailsThunk.rejected, (state, action) => {
                state.loading = false;
            })

            .addCase(getExcessEnquiriesSlice.pending, (state) => {
                state.loading = true;
            })
            .addCase(getExcessEnquiriesSlice.fulfilled, (state, action) => {
                state.loading = false;
                state.bomEnquiries = action.payload
            })
            .addCase(getExcessEnquiriesSlice.rejected, (state, action) => {
                state.loading = false;
            })


            .addCase(getExcessEnquirydetailsThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getExcessEnquirydetailsThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.bomenquirydetails = action.payload
            })
            .addCase(getExcessEnquirydetailsThunk.rejected, (state, action) => {
                state.loading = false;
            })


    },
})

export const { setBomPreview, deleteAlterProduct, deletBomPreviewData, setFileName, setInputValue, deleteInputValue, setQuantities, toggleProductSelection, setSelectedProducts, setMatchedFallbackProduct, setBomPage, setBomEnquiry, updateQuantity,setActiveTab } = bomSlice.actions;
export default bomSlice.reducer
